import { T } from '@/lib/theme'

/// Lines render as a short bar, markers as a dot - matching how each is actually drawn, so the
/// legend is readable without a caption explaining the legend.
function legendItems(lines, markers) {
  return [
    ...lines.filter((l) => l.label).map((l) => ({ label: l.label, color: l.color, isLine: true })),
    ...markers.filter((m) => m.label).map((m) => ({ label: m.label, color: m.color, isLine: false })),
  ]
}

export const CURVE_W = 1000
export const CURVE_H = 220
const PAD_X = 8
const PAD_Y = 12
const Y_AXIS_W = 56

/// A FUNCTION plot, deliberately a sibling of MiniLineChart rather than a fork of it.
///
/// MiniLineChart draws a time series: x is the index of a real observation, and its own doc comment
/// records why ticks land on real points (this app does not fabricate data, and an evenly-spaced
/// calendar tick would have to interpolate between observations). Here x is a continuous VALUE -
/// utilisation, or leverage - and every point is computed from a formula the contract itself
/// evaluates, so there is nothing to fabricate and evenly-spaced ticks are exactly right.
///
/// That difference is the whole reason this exists separately. Reusing MiniLineChart would have
/// meant either passing fake timestamps or teaching it two incompatible x-axis meanings, and the
/// second source of truth that produces is the failure this codebase has already had once with
/// icon resolvers (see TokenGlyph's own note in MarketsSection.jsx).
///
/// `lines`: `[{ points: [{x, y}], color, label, dashed }]`, every line sharing one scale so they
/// stay directly comparable - drawing the borrow and supply curves together is the point, because
/// the GAP between them is the reserve factor, which is otherwise an abstract percentage in a
/// table. `points` is accepted as shorthand for the single-line case.
///
/// `markers`: vertical rules with a dot and a label, used for "you are here" and "this is where
/// your input moves it". `bands`: shaded x-ranges, used to show the region past the kink.
export function CurveChart({
  points,
  lines,
  markers = [],
  bands = [],
  formatX = (v) => v.toFixed(0),
  formatY = (v) => v.toFixed(1),
  height = CURVE_H,
  xTickCount = 5,
}) {
  const allLines = lines ?? (points ? [{ points, color: T.accent }] : [])
  const drawable = allLines.filter((l) => l.points && l.points.length >= 2)
  if (drawable.length === 0) return null

  const xs = drawable.flatMap((l) => l.points.map((p) => p.x))
  const ys = drawable.flatMap((l) => l.points.map((p) => p.y))
  const markerYs = markers.map((m) => m.y).filter((v) => Number.isFinite(v))

  const minX = Math.min(...xs)
  const maxX = Math.max(...xs)
  // Markers are included in the y-scale on purpose: a projected point that lands outside the
  // curve's own range would otherwise be drawn off-canvas, silently, which is worse than a
  // slightly looser scale.
  const rawMinY = Math.min(...ys, ...markerYs)
  const rawMaxY = Math.max(...ys, ...markerYs)
  // Zero is included whenever the data straddles it or sits near it, so a curve crossing into
  // negative territory shows the crossing rather than floating above an implied baseline. This is
  // the case that matters for Multiply, where a negative net APY is the single most important
  // thing the chart can say.
  const minY = Math.min(rawMinY, 0)
  const maxY = Math.max(rawMaxY, 0)
  const spanY = maxY - minY || 1
  const spanX = maxX - minX || 1

  const toX = (x) => PAD_X + ((x - minX) / spanX) * (CURVE_W - PAD_X * 2)
  const toY = (y) => CURVE_H - PAD_Y - ((y - minY) / spanY) * (CURVE_H - PAD_Y * 2)

  const paths = drawable.map((l) => ({
    ...l,
    d: l.points
      .map((p, i) => `${i === 0 ? 'M' : 'L'}${toX(p.x).toFixed(2)},${toY(p.y).toFixed(2)}`)
      .join(' '),
  }))

  const zeroY = toY(0)
  const showZeroLine = minY < 0 && maxY > 0

  const xTicks = []
  for (let i = 0; i < xTickCount; i++) xTicks.push(minX + (spanX * i) / (xTickCount - 1))
  const yTicks = [maxY, (maxY + minY) / 2, minY]

  return (
    <div style={{ display: 'flex', gap: 10 }}>
      <div style={{ position: 'relative', width: Y_AXIS_W, flexShrink: 0, height }}>
        {yTicks.map((v, i) => (
          <span key={i} style={{
            position: 'absolute', right: 0, top: `${(i / (yTicks.length - 1)) * 100}%`,
            transform: i === 0 ? 'translateY(0)' : i === yTicks.length - 1 ? 'translateY(-100%)' : 'translateY(-50%)',
            color: T.textDim, fontFamily: T.fontM, fontSize: 10, whiteSpace: 'nowrap',
          }}>
            {formatY(v)}
          </span>
        ))}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <svg viewBox={`0 0 ${CURVE_W} ${CURVE_H}`} preserveAspectRatio="none" style={{ width: '100%', height, display: 'block', overflow: 'visible' }}>
          {bands.map((b, i) => (
            <rect key={`band-${i}`} x={toX(b.from)} y={PAD_Y} width={Math.max(0, toX(b.to) - toX(b.from))}
              height={CURVE_H - PAD_Y * 2} fill={b.color ?? T.warning} opacity={0.07} />
          ))}

          {showZeroLine && (
            <line x1={PAD_X} x2={CURVE_W - PAD_X} y1={zeroY} y2={zeroY}
              stroke={T.textDim} strokeWidth={1} strokeDasharray="4 4" opacity={0.6} />
          )}

          {paths.map((l, i) => (
            <path key={`line-${i}`} d={l.d} fill="none" stroke={l.color ?? T.accent} strokeWidth={2.5}
              strokeDasharray={l.dashed ? '6 4' : undefined}
              strokeLinejoin="round" strokeLinecap="round" />
          ))}

          {markers.map((m, i) => {
            const mx = toX(m.x)
            const my = toY(m.y)
            return (
              <g key={`marker-${i}`}>
                <line x1={mx} x2={mx} y1={PAD_Y} y2={CURVE_H - PAD_Y}
                  stroke={m.color ?? T.text} strokeWidth={1.5}
                  strokeDasharray={m.dashed ? '5 4' : undefined} opacity={m.dashed ? 0.7 : 0.5} />
                <circle cx={mx} cy={my} r={5} fill={m.color ?? T.text} stroke={T.bg2} strokeWidth={2} />
              </g>
            )
          })}
        </svg>

        <div style={{ position: 'relative', height: 16, marginTop: 8 }}>
          {xTicks.map((v, i) => {
            const xPct = (toX(v) / CURVE_W) * 100
            const isFirst = i === 0
            const isLast = i === xTicks.length - 1
            return (
              <span key={i} style={{
                position: 'absolute', left: `${xPct}%`, top: 0,
                transform: isFirst ? 'translateX(0)' : isLast ? 'translateX(-100%)' : 'translateX(-50%)',
                color: T.textDim, fontFamily: T.fontM, fontSize: 10, whiteSpace: 'nowrap',
              }}>
                {formatX(v)}
              </span>
            )
          })}
        </div>

        {(legendItems(drawable, markers)).length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 10 }}>
            {legendItems(drawable, markers).map((m, i) => (
              <span key={`legend-${i}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.caption }}>
                <span style={{
                  width: 10, height: m.isLine ? 2 : 8, borderRadius: m.isLine ? 1 : '50%',
                  background: m.color ?? T.text, flexShrink: 0,
                }} />
                {m.label}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
